import {useEffect, useRef} from "react";
import {ShopSidebar} from "./ShopSidebar";
import {ShopPlacemark} from "./ShopPlacemark";
import {Shop, SHOP_TYPE, ShopType} from "@/entities/shop";
import {pageEvents} from "@/shared/lib/events";
import {Button} from "@/shared/ui/Button";
import {YandexMap, YandexMapInstance, YandexMapsProvider, YandexPlacemarkActions} from "@/shared/ui/YandexMap";
import styles from "./ShopMap.module.scss";

type MapDefaultState = {
    geo_lat: number;
    geo_lon: number;
    zoom: number;
}

type Props = {
    shops: Shop[];
    shopType: ShopType;
    mapDefaultState: MapDefaultState;
    query: string;
    loading: boolean;
    setQuery: (query: string) => void;
    fetchShops: () => void;
    changeShopType: (shopType: ShopType) => void;
    isMobile: boolean;
}

export const ShopMap = (
    {
        shops,
        shopType,
        mapDefaultState,
        query,
        loading,
        setQuery,
        fetchShops,
        changeShopType,
        isMobile,
    }: Props
) => {
    const mapRef = useRef<YandexMapInstance | null>(null);
    const placemarkRefs = useRef<Record<string, YandexPlacemarkActions | null>>({});
    const ref = useRef<HTMLDivElement>(null);

    const buttonSize = isMobile ? "m" : "xxl";

    /**
     * Подписка на событие скрола
     */
    useEffect(() => {
        const listener = () => {
            ref.current?.scrollIntoView({
                behavior: "smooth",
            });
        };

        pageEvents.scrollToMap.on(() => {
            listener();
        });
    }, []);

    const handleShowOnMap = (shop: Shop) => {
        mapRef.current?.setCenter(
            [shop.targetx, shop.targety],
            15,
        );

        placemarkRefs.current[shop.id]?.openBalloon();

        if (isMobile) {
            pageEvents.scrollToMap.emit();
        }
    };

    /**
     * Фильтр (переключение типа магазина)
     * @param shopType
     */
    const onChangeShop = (shopType) => {
        changeShopType(shopType);

        fetchShops();
    }

    return (
        <div className={styles.container}>
            <div className={styles.mapFilter}>
                <Button
                    onClick={() => onChangeShop(SHOP_TYPE.BRANDED)}
                    variant={`${shopType === SHOP_TYPE.BRANDED ? `primary` : `tertiary`}`}
                    className={styles.mapFilterButton}
                    size={buttonSize}
                >
                    Фирменные магазины
                </Button>
                <Button
                    onClick={() => onChangeShop(SHOP_TYPE.VIP_PARTNER)}
                    variant={`${shopType === SHOP_TYPE.VIP_PARTNER ? `primary` : `tertiary`}`}
                    className={styles.mapFilterButton}
                    size={buttonSize}
                >
                    Магазины партнеров
                </Button>
                <Button
                    onClick={() => onChangeShop(SHOP_TYPE.PARTNER)}
                    variant={`${shopType === SHOP_TYPE.PARTNER ? `primary` : `tertiary`}`}
                    className={styles.mapFilterButton}
                    size={buttonSize}
                >
                    Все
                </Button>
            </div>
            <div ref={ref} className={styles.mapContainer}>
                <ShopSidebar
                    shops={shops}
                    query={query}
                    loading={loading}
                    setQuery={setQuery}
                    fetchShops={fetchShops}
                    onShowOnMap={handleShowOnMap}
                />
                <YandexMapsProvider>
                    <YandexMap
                        instanceRef={mapRef}
                        state={{
                            center: [mapDefaultState.geo_lat, mapDefaultState.geo_lon],
                            zoom: mapDefaultState.zoom
                        }}
                        className={styles.map}
                    >
                        {shops.map(shop => (
                            <ShopPlacemark
                                key={shop.id}
                                shop={shop}
                                onActionsReady={(actions) => {
                                    placemarkRefs.current[String(shop.id)] = actions;
                                }}
                            />
                        ))}
                    </YandexMap>
                </YandexMapsProvider>
            </div>
        </div>
    );
}
